import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { ADMIN_GROUP, DashboardGroup, NAVIGATION_ITEMS, NavigationItem } from './auth-groups';

interface TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in: number;
  token_type: string;
}

interface StoredSession {
  accessToken: string;
  idToken?: string;
  refreshToken?: string;
  expiresAt: number;
  issuer: string;
  clientId: string;
}

interface OidcMetadata {
  authorization_endpoint: string;
  token_endpoint: string;
}

interface OAuthEndpoints {
  authorize: string;
  token: string;
  logout: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly sessionKey = 'hpg.auth.session';
  private readonly verifierKey = 'hpg.auth.pkce.verifier';
  private readonly stateKey = 'hpg.auth.pkce.state';
  private endpoints?: Promise<OAuthEndpoints>;
  private navigationCache?: { groupsKey: string; items: NavigationItem[] };

  constructor(private readonly router: Router) {}

  get accessToken(): string | null {
    return this.session?.accessToken ?? null;
  }

  get isAuthenticated(): boolean {
    const session = this.session;
    return !!session?.accessToken && Date.now() < session.expiresAt - 30_000;
  }

  get userEmail(): string {
    const payload = this.decodeJwt(this.session?.idToken);
    return String(payload?.['email'] ?? payload?.['cognito:username'] ?? 'Operador');
  }

  get groups(): string[] {
    const payload = this.decodeJwt(this.session?.accessToken);
    const groups = payload?.['cognito:groups'];
    return Array.isArray(groups) ? groups.filter((group): group is string => typeof group === 'string') : [];
  }

  get allowedNavigationItems(): NavigationItem[] {
    const groups = this.groups;
    const groupsKey = [...groups].sort().join('|');
    if (this.navigationCache?.groupsKey !== groupsKey) {
      this.navigationCache = {
        groupsKey,
        items: this.filterNavigationItems(NAVIGATION_ITEMS, new Set(groups))
      };
    }
    return this.navigationCache.items;
  }

  private filterNavigationItems(items: NavigationItem[], groups: Set<string>): NavigationItem[] {
    const isAdmin = groups.has(ADMIN_GROUP);
    return items
      .filter(item => !item.group || isAdmin || groups.has(item.group))
      .map(item => ({
        ...item,
        children: item.children ? this.filterNavigationItems(item.children, groups) : undefined
      }))
      .filter(item => !item.children || item.children.length > 0);
  }

  hasGroup(group: DashboardGroup): boolean {
    return this.groups.includes(ADMIN_GROUP) || this.groups.includes(group);
  }

  hasAnyGroup(groups: DashboardGroup[]): boolean {
    return this.groups.includes(ADMIN_GROUP) || groups.some(group => this.groups.includes(group));
  }

  private get redirectUri(): string {
    return environment.cognito.redirectUri;
  }

  private get logoutUri(): string {
    return environment.cognito.logoutUri;
  }

  async login(): Promise<void> {
    const endpoints = await this.getOAuthEndpoints();
    const verifier = this.randomString(64);
    const state = this.randomString(32);
    const challenge = await this.pkceChallenge(verifier);

    this.clearPkceState();
    sessionStorage.setItem(this.verifierKey, verifier);
    sessionStorage.setItem(this.stateKey, state);

    const params = new URLSearchParams({
      client_id: environment.cognito.clientId,
      response_type: 'code',
      scope: environment.cognito.scopes,
      redirect_uri: this.redirectUri,
      code_challenge_method: 'S256',
      code_challenge: challenge,
      state
    });

    window.location.href = `${endpoints.authorize}?${params.toString()}`;
  }

  async handleCallback(code: string, state: string): Promise<void> {
    const expectedState = sessionStorage.getItem(this.stateKey);
    const verifier = sessionStorage.getItem(this.verifierKey);

    if (!verifier || !expectedState || expectedState !== state) {
      throw new Error('El callback de Cognito no coincide con la sesion iniciada.');
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: environment.cognito.clientId,
      code,
      redirect_uri: this.redirectUri,
      code_verifier: verifier
    });

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 15_000);

    let response: Response;
    try {
      const endpoints = await this.getOAuthEndpoints();
      response = await fetch(endpoints.token, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
        signal: controller.signal
      });
    } finally {
      window.clearTimeout(timeout);
    }

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `No se pudo intercambiar el codigo de Cognito por tokens. ${errorText || response.statusText}`
      );
    }

    const tokens = (await response.json()) as TokenResponse;
    this.storeSession(tokens);
    sessionStorage.removeItem(this.verifierKey);
    sessionStorage.removeItem(this.stateKey);
  }

  async logout(): Promise<void> {
    this.clearSession();
    const params = new URLSearchParams({
      client_id: environment.cognito.clientId,
      logout_uri: this.logoutUri
    });
    const endpoints = await this.getOAuthEndpoints();
    window.location.href = `${endpoints.logout}?${params.toString()}`;
  }

  private getOAuthEndpoints(): Promise<OAuthEndpoints> {
    this.endpoints ??= this.discoverOAuthEndpoints();
    return this.endpoints;
  }

  private async discoverOAuthEndpoints(): Promise<OAuthEndpoints> {
    if (environment.cognito.domain) {
      return {
        authorize: `${environment.cognito.domain}/oauth2/authorize`,
        token: `${environment.cognito.domain}/oauth2/token`,
        logout: `${environment.cognito.domain}/logout`
      };
    }

    const response = await fetch(`${environment.cognito.issuer}/.well-known/openid-configuration`);
    if (!response.ok) {
      throw new Error('No se pudo descubrir la configuracion OIDC de Cognito.');
    }

    const metadata = (await response.json()) as OidcMetadata;
    const hostedUiOrigin = new URL(metadata.authorization_endpoint).origin;

    return {
      authorize: metadata.authorization_endpoint,
      token: metadata.token_endpoint,
      logout: `${hostedUiOrigin}/logout`
    };
  }

  private get session(): StoredSession | null {
    const raw = localStorage.getItem(this.sessionKey);
    if (!raw) {
      return null;
    }

    try {
      const session = JSON.parse(raw) as Partial<StoredSession>;
      if (!this.isValidSession(session)) {
        this.clearSession();
        return null;
      }
      return session as StoredSession;
    } catch {
      this.clearSession();
      return null;
    }
  }

  private storeSession(tokens: TokenResponse): void {
    const accessTokenPayload = this.decodeJwt(tokens.access_token);
    const session: StoredSession = {
      accessToken: tokens.access_token,
      idToken: tokens.id_token,
      refreshToken: tokens.refresh_token,
      expiresAt: this.tokenExpiresAt(accessTokenPayload) ?? Date.now() + tokens.expires_in * 1000,
      issuer: environment.cognito.issuer,
      clientId: environment.cognito.clientId
    };
    localStorage.setItem(this.sessionKey, JSON.stringify(session));
  }

  clearSession(): void {
    localStorage.removeItem(this.sessionKey);
    this.clearPkceState();
  }

  private clearPkceState(): void {
    sessionStorage.removeItem(this.verifierKey);
    sessionStorage.removeItem(this.stateKey);
  }

  private isValidSession(session: Partial<StoredSession>): boolean {
    if (!session.accessToken || typeof session.accessToken !== 'string') {
      return false;
    }
    if (!session.expiresAt || Date.now() >= Number(session.expiresAt) - 30_000) {
      return false;
    }
    if (session.issuer && session.issuer !== environment.cognito.issuer) {
      return false;
    }
    if (session.clientId && session.clientId !== environment.cognito.clientId) {
      return false;
    }

    const payload = this.decodeJwt(session.accessToken);
    if (!payload) {
      return false;
    }
    if (payload['iss'] !== environment.cognito.issuer) {
      return false;
    }
    if (payload['client_id'] !== environment.cognito.clientId) {
      return false;
    }

    const tokenExpiresAt = this.tokenExpiresAt(payload);
    return !tokenExpiresAt || Date.now() < tokenExpiresAt - 30_000;
  }

  private tokenExpiresAt(payload: Record<string, unknown> | null): number | null {
    const exp = payload?.['exp'];
    return typeof exp === 'number' ? exp * 1000 : null;
  }

  private async pkceChallenge(verifier: string): Promise<string> {
    const bytes = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return this.base64Url(new Uint8Array(digest));
  }

  private randomString(length: number): string {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return this.base64Url(bytes).slice(0, length);
  }

  private base64Url(bytes: Uint8Array): string {
    let binary = '';
    bytes.forEach(byte => (binary += String.fromCharCode(byte)));
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private decodeJwt(token?: string): Record<string, unknown> | null {
    if (!token) return null;
    const [, payload] = token.split('.');
    if (!payload) return null;
    try {
      return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }
}
